import {
  AdjacentType,
  ComparisonType,
  DatastoreObjectType,
  DatastoreObjectTypes,
  DeviceTypes,
  EdgeParams,
  ExpressionFunctionType,
  GeoGrid,
  LogicalOperatorType,
  Networks,
  Position,
  ValidBusinessObject,
  ValidBusinessObjectList,
  getPredictableZombie,
  isAreaWithGrids,
  isIGeofence,
  isNode,
  isNonNaNNumber,
  isValidBusinessObject,
  isValidGeoObject,
} from '@iotv/iotv-v3-types'
import { getPredictableDeviceZombies, getPredictableZombies, getZombieEdge, getZombieInstanceFromPrimaryKey } from '../../TypeHelpers'
import { CustomerAccountType } from '../../../types/ModelTypes'
import {
  ValidModelObject,
  ValidModelObjects,
  ValidModelType,
  edgeTypeMatrix,
  getChildEdgeTypeId,
  getEntityToPlaceEdgeTypeId,
  getGroupToTypeEdgeTypeMatrixEntry,
  getPropertyToEntityEdgeTypeMatrixEntry,
} from '../../../classDependentTypes'
import { Group } from '../../../model'
import { convertTurfPointsToGLatLng, extendPointToPoints, getRandomPointInPoints } from '../../geo'
import { IAWOLEnabled } from '../../../model/Interfaces'
import type { Position as TurfPosition } from 'geojson'
import { center, points } from '@turf/turf'

export type GetNewContextInputOptionals = {
  apexCustomer?: ValidBusinessObject
  apexCustomerGroup?: ValidBusinessObject
  targetCustomer?: ValidBusinessObject
  targetProperty?: ValidBusinessObject
  targetArea?: ValidBusinessObject
}

export type GetNewContextInput = {
  deviceType: DeviceTypes
  deviceEnabledTypeId: string
  appID?: string
  networkKey?: Networks
  numberOfDevices?: number | undefined
  devEuiLength?: 8 | 16
  deviceEUIStart?: number
} & GetNewContextInputOptionals &
  IAWOLEnabled

/** creates a mock customer A, group, Customer B group Device structure with n devices.  returns device, thing, group and customers as list  */
export const getNewEntityContext = ({
  numberOfDevices = 1,
  networkKey = Networks.Interceptor,
  devEuiLength = 16,
  deviceEUIStart = 0x00000123,
  _permittedAWOL,
  deviceType,
  deviceEnabledTypeId,
  appID = 'mock',
  apexCustomer,
  apexCustomerGroup,
  targetCustomer,
  targetProperty,
  targetArea,
}: GetNewContextInput) => {
  const providedAreaBounds: Position[] | undefined = isIGeofence(targetArea) ? targetArea.points : undefined
  let targetCells: GeoGrid['cells'] | undefined = undefined
  if (isAreaWithGrids(targetArea)) {
    const { metadata } = targetArea
    const { grids } = metadata
    const gridsArr = Object.values(grids)
    const targetGrid = gridsArr.at(0)
    if (targetGrid) {
      const { cells } = targetGrid
      targetCells = cells
    }
  }
  if (targetCells) {
    console.log(`mutating number of devices to match discovered cells in grid ${targetCells.length}`)
    numberOfDevices = targetCells.length
  }

  const bounds = providedAreaBounds ?? extendPointToPoints({ lat: 50.813676, lng: -2.4747 }, 1000)
  const zombieDevices = getPredictableDeviceZombies({
    deviceType,
    appID,
    networkKey,
    numberOfDevices,
    devEuiLength,
    deviceEUIStart,
    _permittedAWOL,
  }) as ValidModelObjects<'Device'>

  const zombieEntities = getPredictableZombies(deviceEnabledTypeId, numberOfDevices)

  const zombieProperty: ValidBusinessObject = targetProperty ? targetProperty : getPredictableZombie('Property', 1, { name: 'TestPrimaryCustomerGroup' })
  if (!zombieProperty.location) {
    zombieProperty.location = getRandomPointInPoints(bounds)
  }

  const zombiePlaces = getPredictableZombies('Place', numberOfDevices)
  zombiePlaces.forEach((z, i) => {
    const correspondingCell = targetCells?.[i]
    if (isIGeofence(correspondingCell)) {
      const pArray: TurfPosition[] = correspondingCell.points
        .filter(({ lat, lng }) => isNonNaNNumber(lat) && isNonNaNNumber(lng))
        .map(({ lat, lng }) => [lng, lat])
      const xCenter: TurfPosition = center(points(pArray)).geometry.coordinates
      z.location = convertTurfPointsToGLatLng(xCenter)
    } else {
      z.location = getRandomPointInPoints(bounds)
    }
  })

  const entityPlaceEdges = zombieEntities
    .map((e, i) => {
      const place = zombiePlaces.at(i)
      if (place) {
        return getZombieEdge(e, place, getEntityToPlaceEdgeTypeId(e.type)) //cant rely on datamodel constructed edgeTypeIds here
      } else return null
    })
    .filter(isValidBusinessObject) as ValidBusinessObjectList

  const primaryCustomer = apexCustomer ?? getZombieInstanceFromPrimaryKey('Customer:A', { name: 'TestPrimaryCustomer', accountType: CustomerAccountType.X })
  const primaryGroup = apexCustomerGroup ?? getZombieInstanceFromPrimaryKey('Group:A', { name: 'TestPrimaryCustomerGroup', supportedType: 'Customer' })
  const subCustomer = targetCustomer ?? getZombieInstanceFromPrimaryKey('Customer:B', { name: 'TestCustomer', accountType: CustomerAccountType.Y })
  const supportedTypeDevice: Group['supportedType'] = 'Device'
  const deviceGroup = getZombieInstanceFromPrimaryKey('Group:B', { name: 'TestCustomerDeviceGroup', supportedType: supportedTypeDevice })
  const supportedTypeEntity: Group['supportedType'] = deviceEnabledTypeId as ValidModelType
  const entityGroup = getZombieInstanceFromPrimaryKey('Group:C', { name: 'TestCustomerEntityGroup', supportedType: supportedTypeEntity })
  const deviceEntityEdges = zombieEntities.map((entity, i) => {
    return getZombieEdge(entity, zombieDevices[i])
  })

  const entityPropertyEdges = zombieEntities.map((entity, i) => {
    return getZombieEdge(zombieProperty, entity, `property_has_${deviceEnabledTypeId}`)
  })
  const entityGroupEdges = zombieEntities.map((entity, i) => {
    return getZombieEdge(entityGroup, entity, `group_has_${deviceEnabledTypeId}`)
  })
  const areaPlaceEdges = targetArea ? zombiePlaces.map((place) => getZombieEdge(targetArea, place, edgeTypeMatrix.Area.Place.area_has_place.edgeTypeId)) : []
  const customerCustomerGroupEdge = getZombieEdge(primaryCustomer, primaryGroup, edgeTypeMatrix.Customer.Group.customer_has_group.edgeTypeId)
  const customerGroupDeviceGroupEdge = getZombieEdge(primaryGroup, subCustomer, edgeTypeMatrix.Group.Customer.group_has_customer.edgeTypeId)
  const customerDeviceGroupEdge = getZombieEdge(subCustomer, deviceGroup, edgeTypeMatrix.Customer.Group.customer_has_group.edgeTypeId)
  const customerPropertyEdge = getZombieEdge(subCustomer, zombieProperty, edgeTypeMatrix.Customer.Property.customerHasProperty.edgeTypeId)
  const customerEntityGroupEdge = getZombieEdge(subCustomer, entityGroup, edgeTypeMatrix.Customer.Group.customer_has_group.edgeTypeId)

  const customerDeviceInventoryEdges = zombieDevices.map((device: ValidModelObject<'Device'>) =>
    getZombieEdge(primaryCustomer, device, edgeTypeMatrix.Customer.Device.customer_device_inventory_includes.edgeTypeId),
  )
  const customerDeviceAllocationEdges = zombieDevices.map((device: ValidModelObject<'Device'>) =>
    getZombieEdge(subCustomer, device, edgeTypeMatrix.Customer.Device.customer_has_been_allocated_device.edgeTypeId),
  )
  const groupDeviceEdges = zombieDevices.map((device: ValidModelObject<'Device'>) =>
    getZombieEdge(deviceGroup, device, edgeTypeMatrix.Group.Device.group_has_device.edgeTypeId),
  )

  const context: ValidBusinessObjectList = [
    ...zombieDevices,
    ...zombieEntities,
    ...zombiePlaces,
    ...deviceEntityEdges,
    subCustomer,
    deviceGroup,
    entityGroup,
    customerDeviceGroupEdge,
    ...groupDeviceEdges,
    primaryCustomer,
    primaryGroup,
    customerCustomerGroupEdge,
    customerGroupDeviceGroupEdge,
    ...customerDeviceAllocationEdges,
    ...customerDeviceInventoryEdges,
    zombieProperty,
    ...entityPropertyEdges,
    customerPropertyEdge,
    ...entityGroupEdges,
    customerEntityGroupEdge,
    ...entityPlaceEdges,
    ...areaPlaceEdges,
  ]

  if (targetArea) {
    console.log('pushing area', targetArea)
    context.push(targetArea)
  }

  return context
}
